import { DATE_FORMAT_WEEK_DAY_MID_FNS, PeriodTypeEnum, WeeklySchedule } from '@schooly/api';
import { buildTestId, TIMETABLE } from '@schooly/constants/test-ids';
import { GridColumn, ScheduleGridItemData } from '@schooly/style';
import { isNotEmpty } from '@schooly/utils/predicates';
import { useMemo, useState } from 'react';

import { getUserInitials } from '../helpers/users';
import {
  DEFAULT_TIME_RANGE,
  getScheduleTimeRange,
  ScheduleGroupWithRoom,
  ScheduleItemData,
} from '../pages/ProfileModal/tabs/ProfileModalSchedule/utils';
import { ASSIGNABLE_PERIODS } from '../pages/School/SchoolTimetables/CreateTimetable/TimetableSlotCard';
import { PERIOD_TYPE_COLOR_PRESETS } from '../pages/School/SchoolTimetables/utils';
import { getWeekdayLabel } from '../pages/School/SchoolTimetableStructures/utils';
import { resolveStructureRegisterSessions } from '../pages/School/SchoolTimetables/register-session-display';

type StructureOption = WeeklySchedule['structure'];

type WeeklyScheduleGridResult = {
  structures: StructureOption[];
  selectedStructure: StructureOption | undefined;
  setSelectedStructureId: (id: string) => void;
  items: ScheduleGridItemData<ScheduleItemData>[];
  days: GridColumn[];
  timeRange: { start: string; end: string };
  hasSchedule: boolean;
};

type UseWeeklyScheduleGridProps = {
  schedule: WeeklySchedule[] | undefined;
  onLabelClick?: (groupId: string) => void;
  transformOptions?: TransformScheduleOptions;
  roomMap: Map<string, string[]>;
  getTimeRange?: typeof getScheduleTimeRange;
};

export function useWeeklyScheduleGrid({
  schedule,
  onLabelClick,
  transformOptions,
  roomMap,
  getTimeRange = getScheduleTimeRange,
}: UseWeeklyScheduleGridProps): WeeklyScheduleGridResult {
  const [selectedStructureId, setSelectedStructureId] = useState<string | null>(null);

  const structures = useMemo(() => schedule?.map((s) => s.structure) ?? [], [schedule]);

  const selectedStructure = useMemo(
    () => structures.find((s) => s.id === selectedStructureId) ?? structures[0],
    [structures, selectedStructureId],
  );

  const currentSchedule = useMemo(
    () => schedule?.find((s) => s.structure.id === selectedStructure?.id),
    [schedule, selectedStructure],
  );

  const items = useMemo(
    () =>
      currentSchedule
        ? transformScheduleToItems({
            schedule: currentSchedule,
            onLabelClick,
            transformOptions,
            roomMap,
          })
        : [],
    [currentSchedule, onLabelClick, transformOptions, roomMap],
  );

  const days = useMemo<GridColumn[]>(
    () =>
      selectedStructure?.weekdays.map((weekday) => ({
        label: getWeekdayLabel(weekday, DATE_FORMAT_WEEK_DAY_MID_FNS),
        value: weekday,
      })) ?? [],
    [selectedStructure],
  );

  const timeRange = useMemo(
    () => (selectedStructure ? getTimeRange(selectedStructure.periods) : DEFAULT_TIME_RANGE),
    [selectedStructure, getTimeRange],
  );

  return {
    structures,
    selectedStructure,
    setSelectedStructureId,
    items,
    days,
    timeRange,
    hasSchedule: !!currentSchedule,
  };
}

export type TransformScheduleOptions = {
  allowedPeriodTypes?: PeriodTypeEnum[];
};

type TransformScheduleToItemsProps = {
  schedule: WeeklySchedule;
  onLabelClick?: (groupId: string) => void;
  transformOptions?: TransformScheduleOptions;
  roomMap: Map<string, string[]>;
};

export function transformScheduleToItems({
  schedule,
  onLabelClick,
  transformOptions,
  roomMap,
}: TransformScheduleToItemsProps): ScheduleGridItemData<ScheduleItemData>[] {
  const { structure, groups, schedule: weeklySlots } = schedule;
  const { allowedPeriodTypes } = transformOptions ?? {};

  const periodMap = new Map(structure.periods.map((p) => [p.id, p]));
  const registerSessions = resolveStructureRegisterSessions(structure.periods);
  const groupMap = new Map(groups.map((g) => [g.id, g]));
  const slotMap = new Map(
    weeklySlots.flatMap((day) => day.slots.map((slot) => [slot.time_slot_id, slot] as const)),
  );

  return weeklySlots.flatMap((daySchedule) => {
    const weekdayTimeSlots = structure.time_slots.filter(
      (ts) => ts.weekday === daySchedule.weekday,
    );

    return weekdayTimeSlots
      .map((ts) => {
        const period = periodMap.get(ts.period_id);

        if (!period) return null;
        if (allowedPeriodTypes && !allowedPeriodTypes.includes(period.type)) return null;

        const session = registerSessions.get(period.id) ?? null;
        const isAssignable = ASSIGNABLE_PERIODS.includes(period.type);
        const allocations = slotMap.get(ts.id)?.allocations ?? [];
        const groupIds = allocations.map(({ group_id }) => group_id);
        const slotGroups = groupIds.map((id) => groupMap.get(id)).filter(isNotEmpty);

        if (isAssignable && slotGroups.length === 0) return null;

        const isSingleGroup = slotGroups.length === 1;
        const hasFacilities = roomMap.size > 0;
        const groupsWithRoom: ScheduleGroupWithRoom[] = slotGroups.map((group, i) => {
          if (!hasFacilities) return { ...group };
          const roomId = allocations[i]?.room_id ?? null;
          return { ...group, room: roomId ? (roomMap.get(roomId) ?? null) : null };
        });

        const staffCount = slotGroups.reduce((n, g) => n + g.staff_preview.length, 0) || undefined;
        const staffLabel =
          isSingleGroup && staffCount === 1
            ? getUserInitials(slotGroups[0].staff_preview[0])
            : staffCount;
        const roomPath = isSingleGroup ? groupsWithRoom[0].room : undefined;
        const roomLabel = roomPath?.at(-1);

        return {
          id: isAssignable
            ? `${daySchedule.weekday}-${ts.id}-${groupIds.join('-')}`
            : `${daySchedule.weekday}-${ts.id}`,
          day: daySchedule.weekday,
          startTime: ts.start_time,
          endTime: ts.end_time,
          dataTestId: buildTestId(
            TIMETABLE.SLOT_PREFIX,
            {
              name: `${getWeekdayLabel(
                daySchedule.weekday,
                DATE_FORMAT_WEEK_DAY_MID_FNS,
              ).toUpperCase()}-${ts.start_time} – ${ts.end_time}`,
            },
            'name',
          ),
          color: PERIOD_TYPE_COLOR_PRESETS[period.type],
          twoToneColor:
            session && period.type === PeriodTypeEnum.Lesson
              ? PERIOD_TYPE_COLOR_PRESETS[PeriodTypeEnum.Registration]
              : undefined,
          label: isAssignable ? slotGroups.map((g) => g.name) : period.name,
          onLabelClick:
            isSingleGroup && isAssignable && onLabelClick
              ? () => onLabelClick(slotGroups[0].id)
              : undefined,
          data: {
            groupsWithRoom,
            periodId: period.id,
            periodType: period.type,
            onGroupClick: isAssignable && onLabelClick ? onLabelClick : undefined,
            roomLabel,
            staffLabel,
            registerSession: session,
          },
        };
      })
      .filter(isNotEmpty);
  });
}
